import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type FC,
    type ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme, type ThemeMode } from './index';

/** What the user chose. `system` follows the OS setting and keeps following it. */
export type ColorModePreference = ThemeMode | 'system';

const STORAGE_KEY = 'colorMode';
const QUERY = '(prefers-color-scheme: dark)';

const isPreference = (v: unknown): v is ColorModePreference =>
    v === 'light' || v === 'dark' || v === 'system';

const readPreference = (): ColorModePreference => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isPreference(stored) ? stored : 'system';
    } catch {
        // Private mode or blocked storage: fall back to following the OS.
        return 'system';
    }
};

const systemMode = (): ThemeMode => {
    try {
        return window.matchMedia(QUERY).matches ? 'dark' : 'light';
    } catch {
        return 'dark';
    }
};

type ColorModeContextValue = {
    /** The user's choice, including `system`. */
    preference: ColorModePreference;
    /** The mode actually being rendered. */
    mode: ThemeMode;
    setPreference: (next: ColorModePreference) => void;
    /** Cycles light -> dark -> system. */
    cycle: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
    preference: 'system',
    mode: 'dark',
    setPreference: () => {},
    cycle: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [preference, setPreferenceState] = useState<ColorModePreference>(readPreference);
    const [system, setSystem] = useState<ThemeMode>(systemMode);

    // Keep following the OS while the preference is `system`.
    useEffect(() => {
        let media: MediaQueryList;
        try {
            media = window.matchMedia(QUERY);
        } catch {
            return;
        }
        const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
        media.addEventListener('change', onChange);
        setSystem(media.matches ? 'dark' : 'light');
        return () => media.removeEventListener('change', onChange);
    }, []);

    const mode: ThemeMode = preference === 'system' ? system : preference;

    const setPreference = useCallback((next: ColorModePreference) => {
        setPreferenceState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Preference simply does not persist if storage is unavailable.
        }
    }, []);

    const cycle = useCallback(() => {
        setPreference(preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light');
    }, [preference, setPreference]);

    // Let the browser paint native controls, scrollbars and form widgets to match.
    useEffect(() => {
        document.documentElement.style.colorScheme = mode;
    }, [mode]);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const value = useMemo<ColorModeContextValue>(
        () => ({ preference, mode, setPreference, cycle }),
        [preference, mode, setPreference, cycle]
    );

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};
